import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex, Text, Stack, HStack, Badge, Icon, Button } from "@chakra-ui/react";
import { AlertTriangle, CalendarClock, FileText, ClipboardList } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { colors } from "@/theme/colors";

const fmtDue = (d) => new Date(d + "T00:00:00").toLocaleDateString([], { month: "short", day: "numeric" });
const today = () => new Date().toISOString().slice(0, 10);

// Manager surfacing of OVERDUE scheduled assignments (forms + acknowledgements).
// Tenant isolation: pinned to the ACTING company — RLS alone returns the union
// of all memberships, which must never render here (Sweep finding A1).
export function NeedsAttention() {
  const navigate = useNavigate();
  const { activeCompanyId } = useAuth();
  const [overdue, setOverdue] = useState([]);
  const [upcoming, setUpcoming] = useState(0);

  const load = useCallback(async () => {
    if (!activeCompanyId) return;
    const t = today();
    const week = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
    const [{ data: od }, { count }] = await Promise.all([
      supabase.from("form_assignments")
        .select("id,title,kind,worker_name,due_date,location_name")
        .eq("company_id", activeCompanyId)
        // Parity with /submissions?status=overdue: once a form is submitted the
        // linked SUBMISSION is the tracked item — the assignment no longer counts.
        .is("submission_id", null)
        .not("status", "in", "(completed,superseded)").lt("due_date", t)
        .order("due_date").limit(50),
      supabase.from("form_assignments")
        .select("id", { count: "exact", head: true })
        .eq("company_id", activeCompanyId)
        .is("submission_id", null)
        .not("status", "in", "(completed,superseded)").gte("due_date", t).lte("due_date", week),
    ]);
    setOverdue(od || []);
    setUpcoming(count || 0);
  }, [activeCompanyId]);
  useEffect(() => { load(); }, [load]);

  if (overdue.length === 0) return null;

  return (
    <Box bg={colors.white} border="1px solid" borderColor={colors.poppy} borderRadius="14px" p="4" mb="6" data-testid="needs-attention">
      <Flex justify="space-between" align="center" gap="3" flexWrap="wrap" mb="2.5">
        <HStack gap="2">
          <Icon as={AlertTriangle} boxSize="4.5" color={colors.poppy} />
          <Text fontWeight="700" fontSize="sm" color={colors.textPrimary} data-testid="needs-attention-count">
            Needs attention — {overdue.length} overdue assignment{overdue.length === 1 ? "" : "s"}
          </Text>
        </HStack>
        <Button size="xs" variant="outline" color={colors.poppy}
          onClick={() => navigate("/submissions?status=overdue")} data-testid="needs-attention-view-all">
          View all
        </Button>
      </Flex>
      <Stack gap="1.5">
        {overdue.slice(0, 5).map((a) => (
          <Flex key={a.id} justify="space-between" align="center" gap="3" px="3" py="2" borderRadius="8px"
            bg={colors.criticalBg} data-testid={`attention-row-${a.id}`}>
            <HStack gap="2" minW="0">
              <Icon as={a.kind === "resource" ? FileText : ClipboardList} boxSize="3.5" color={colors.criticalText} flexShrink={0} />
              <Text fontSize="sm" color={colors.textPrimary} lineClamp={1}>
                <Text as="span" fontWeight="600">{a.title}</Text> — {a.worker_name}
              </Text>
            </HStack>
            <HStack gap="1" flexShrink={0}>
              <Icon as={CalendarClock} boxSize="3" color={colors.criticalText} />
              <Text fontSize="xs" fontWeight="700" color={colors.criticalText}>Due {fmtDue(a.due_date)}</Text>
            </HStack>
          </Flex>
        ))}
        {overdue.length > 5 && (
          <Text fontSize="xs" color={colors.textSecondary}>+ {overdue.length - 5} more overdue</Text>
        )}
      </Stack>
      {upcoming > 0 && (
        <HStack gap="1.5" mt="2.5">
          <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2.5" data-testid="attention-upcoming">
            {upcoming} due in the next 7 days
          </Badge>
        </HStack>
      )}
    </Box>
  );
}
